/**
 * Region awareness for the frontend (LT-98).
 *
 * REGION is set at build time via the REACT_APP_REGION env var (see Dockerfile
 * and cloudbuild-in*.yaml). Defaults to "au" so AU builds are unchanged.
 *
 * Use IS_INDIA to gate region-specific UI (e.g. hide SMS surfaces).
 *
 * Phone helpers below drive strict-mode validation and user-facing examples
 * so the deployment never shows cross-region phone formats (LT-XXX).
 */
export const REGION: 'au' | 'in' = (
  (process.env.REACT_APP_REGION || 'au').toLowerCase() as 'au' | 'in'
);

export const IS_INDIA: boolean = REGION === 'in';
export const IS_AUSTRALIA: boolean = REGION === 'au';

/**
 * Marketing footer entity — region-specific. The IN deployment is operated
 * under the KSVARAMP Voice Tech brand; AU stays Void Inc. The product brand
 * ("Agent Noda" in the logo) is unchanged across regions.
 */
export const COMPANY_NAME: string = IS_INDIA ? 'KSVARAMP Voice Tech™' : 'Void Inc.';

/**
 * Product brand shown in-app (login, breadcrumb, dashboard greeting,
 * onboarding tour). Rebranded to "Agent Noda" by LT-497 and — unlike the
 * legal entity in COMPANY_NAME — unchanged across regions. Centralised here
 * so in-app brand strings never hard-code the legacy "Cold Caller" name again
 * (CCT-3/4/5/13).
 */
export const APP_NAME: string = 'Agent Noda';

/**
 * Transactional email provider name, region-specific. AU sends via SendGrid;
 * IN migrated to Zepto Mail (LT-393/394). Used in user-visible sender /
 * verification copy so the name matches the provider the email actually comes
 * from (CCT-14).
 */
export const EMAIL_PROVIDER_NAME: string = IS_INDIA ? 'Zepto' : 'SendGrid';

/**
 * City/country line for the footer. The IN entity is remote-first with no
 * published address, so it renders the company name only.
 */
export const COMPANY_LOCATION: string = IS_INDIA ? '' : 'Brisbane, Australia';

/** Support contact address shown in the marketing footer. */
export const SUPPORT_EMAIL: string = IS_INDIA ? 'karthik@ksvaramp.com' : 'support@voidinc.ai';

/** ISO-3166 alpha-2 for the deployment region. Use with libphonenumber-js. */
export const REGION_COUNTRY_CODE: 'AU' | 'IN' = IS_INDIA ? 'IN' : 'AU';

/**
 * ISO-3166 alpha-2 codes accepted by phone validators in this deployment.
 * AU bundle accepts both AU and IN (LT-427 — AU sales pipeline includes Indian
 * leads); IN bundle accepts IN only. Order matters for error messages —
 * primary region first.
 */
export const ALLOWED_REGION_COUNTRY_CODES: readonly ('AU' | 'IN')[] = IS_INDIA
  ? ['IN']
  : ['AU', 'IN'];

/** Canonical E.164 example (no spaces) for this region. */
export const PHONE_EXAMPLE_E164: string = IS_INDIA ? '+919876543210' : '+61412345678';

/** Human-friendly example with spaces — for error messages and help text. */
export const PHONE_EXAMPLE_PRETTY: string = IS_INDIA ? '+91 98765 43210' : '+61 412 345 678';

/** Placeholder text for phone inputs. */
export const PHONE_PLACEHOLDER: string = PHONE_EXAMPLE_PRETTY;

/** Mask used by error-row editors that prefer digit obfuscation. */
export const PHONE_PLACEHOLDER_MASK: string = IS_INDIA ? '+91 xxxxx xxxxx' : '+61 xxx xxx xxx';

/** Landline illustrative example (second item in NewRunPage examples list). */
export const PHONE_LANDLINE_EXAMPLE_PRETTY: string = IS_INDIA ? '+91 11 2345 6789' : '+61 2 1234 5678';

/** Full set of format examples (landline, mobile) for UI hints. */
export const PHONE_FORMAT_EXAMPLES: readonly string[] = [
  PHONE_LANDLINE_EXAMPLE_PRETTY,
  PHONE_EXAMPLE_PRETTY,
];

/** Sample CSV rows tailored to the region — used in CSV templates/downloads. */
export const CSV_SAMPLE_ROWS: readonly string[] = IS_INDIA
  ? [
      'John Smith,+91 98765 43210,john.smith@example.com',
      'Sarah Johnson,+91 99876 54321,sarah.johnson@example.com',
      'Michael Brown,+91 11 2345 6789,michael.brown@example.com',
      'Emma Wilson,+91 98123 45678,emma.wilson@example.com',
    ]
  : [
      'John Smith,+61 2 1234 5678,john.smith@example.com',
      'Sarah Johnson,+61 412 345 678,sarah.johnson@example.com',
      'Michael Brown,+61 3 9876 5432,michael.brown@example.com',
      'Emma Wilson,+61 423 456 789,emma.wilson@example.com',
    ];

/** Short noun phrase describing supported numbers — for CSV upload copy. */
export const SUPPORTED_NUMBERS_DESCRIPTION: string = IS_INDIA
  ? 'Indian numbers with +91'
  : 'Australian numbers with +61';

/**
 * Label for the deployment region's local timezone — shown next to
 * schedule time-of-day pickers. Matches the engine's comparison tz.
 */
export const TIMEZONE_LABEL: string = IS_INDIA ? 'IST' : 'Brisbane time';

/**
 * LT-337: region → ISO-4217 currency map. Mirrors the backend constant
 * `_REGION_CURRENCY` in `services/opportunities.py`. Keep both in sync
 * if a third region is ever added.
 */
export const REGION_CURRENCY: Readonly<Record<'au' | 'in', string>> = {
  au: 'AUD',
  in: 'INR',
};

/** Default new-deal currency for the deployment region. AU → AUD, IN → INR. */
export const DEFAULT_CURRENCY: string = REGION_CURRENCY[REGION];

/** Common ISO-4217 codes offered in the deal-creation form. The deployment
 * region's default sits at the top so prefill is visually obvious. */
export const COMMON_CURRENCIES: readonly string[] = (() => {
  const seen = new Set<string>();
  const ordered: string[] = [DEFAULT_CURRENCY];
  seen.add(DEFAULT_CURRENCY);
  for (const code of ['AUD', 'INR', 'USD', 'GBP', 'EUR', 'SGD', 'NZD']) {
    if (!seen.has(code)) {
      seen.add(code);
      ordered.push(code);
    }
  }
  return ordered;
})();
